<template>
  <v-container>
    <v-form v-if="contracts.length > 0" ref="form">
      <v-row>
        <v-col cols="7">
          <v-container class="price-table">
            <v-row justify="start" align="start" align-content="start">
              <v-col>{{$t('book/facility:cost-analysis.contract-type')}}</v-col>
              <v-col>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                        v-bind="attrs"
                        v-on="on"
                    >
                      {{$t('common:cost')}}*
                    </span>
                  </template>
                  <span>{{$t('book/facility:cost-analysis.cost-without-vat')}}</span>
                </v-tooltip>
              </v-col>
              <v-col>{{$t('book/facility:cost-analysis.contract-name')}}</v-col>
              <v-col>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                        v-bind="attrs"
                        v-on="on"
                    >
                      {{$t('book/facility:cost-analysis.actual-cost')}}*
                    </span>
                  </template>
                  <span>{{$t('book/facility:cost-analysis.cost-without-vat')}}</span>
                </v-tooltip>
              </v-col>
              <v-col>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                        v-bind="attrs"
                        v-on="on"
                    >
                      {{$t('common:difference')}}*
                    </span>
                  </template>
                  <span>{{$t('book/facility:cost-analysis.cost-without-vat')}}</span>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row justify="start" align="start" align-content="start" v-for="(contract, index) in contracts" :key="contract.name + index">
              <v-col>{{ contract.name }}</v-col>
              <v-col>{{ contract.cost }}</v-col>
              <v-col>
                <p v-for="(item, contractIndex) in contract.contracts" :key="item.value + contractIndex">
                  <a :href="getContractUrl(item.value) + '?contractId=' + item.value" target="_blank">
                    {{item.key}}
                  </a>
                </p>
              </v-col>
              <v-col>
                <v-text-field
                  @change="updateCharts"
                  v-model="contract.actualCost"
                  outlined
                  :rules="[ruleIsNumber($t('utils:rules.only-number')), ruleNotNegative($t('utils:rules.negative-value'))]"
                  clearable/>
              </v-col>
              <v-col><div :class="getClassColor(contract)">{{(contract.actualCost - contract.cost).toFixed(2) }}</div></v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn @click="saveActualCosts"
                   color="primary"
                   class="mr-3 mb-3 mb-md-0 white--text"
                   elevation="0">
                  {{$t('buttons:save')}}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <v-col cols="5">
          <v-container>
            <v-row>
              <v-col>
                {{$t('book/facility:cost-analysis.total-cost-without-vat')}}
              </v-col>
              <v-col class="primary--text">
                {{totalCost.toFixed(2)}}
              </v-col>
            </v-row>
            <v-row>
              <v-col >
                {{$t('book/facility:cost-analysis.total-cost-with-vat')}}
              </v-col>
              <v-col class="primary--text">
                {{(totalCost + totalVat).toFixed(2)}}
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                {{$t('book/facility:cost-analysis.vat')}}
              </v-col>
              <v-col class="primary--text">
                {{totalVat.toFixed(2)}}
              </v-col>
            </v-row>
            <v-row justify="end">
              <v-col md="1">
                <v-btn
                    elevation="2"
                    icon
                    outlined
                    color="primary"
                    @click="isPieChart = !isPieChart"
                >
                  <v-icon> {{isPieChart ? 'bar_chart' : 'pie_chart'}} </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="isPieChart && showChart">
              <v-col>
                <h3>{{$t('common:cost')}}</h3>
                <VueApexCharts width="500" type="pie" :options="pieChartOptions" :series="pieSeries"></VueApexCharts>
              </v-col>
            </v-row>
            <v-row v-if="isPieChart && showChart && pieManualSeries.length > 0">
              <v-col>
                <h3>{{$t('book/facility:cost-analysis.actual-cost')}}</h3>
                <VueApexCharts width="500" type="pie" :options="pieChartOptions" :series="pieManualSeries"></VueApexCharts>
              </v-col>
            </v-row>
            <v-row v-if="!isPieChart && showChart">
              <v-col>
                <VueApexCharts height="350" type="bar" :options="barChartOptions" :series="barSeries"></VueApexCharts>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
    </v-form>
    <span v-else>
      There is no analysis
    </span>
  </v-container>
</template>

<script>

import VueApexCharts from 'vue-apexcharts'
import CostAnalysisService from '@/modules/book/services/cost-analysis-service';
import Utils from '@/modules/core/services/utils';
import FacilityService from '@/modules/book/services/facility-service';
import {ruleNotNegative, ruleIsNumber} from "@/modules/core/services/rules"

export default {
  name: 'FacilityCostAnalysis',
  components: {VueApexCharts},
  data() {
    return {
      facilityId: '',
      isPieChart: true,
      pieSeries: [],
      pieManualSeries: [],
      barSeries: [],
      pieChartOptions: {
        chart: {
          width: 350,
        },
        labels: [],
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      barChartOptions: {
        chart: {
          height: 450
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          title: {
            text: '€'
          }
        },
        fill: {
          opacity: 1
        }
      },
      contracts: [],
      showChart: false,
      totalCost: 0.0,
      totalVat: 0.0
    }
  },
  methods: {
    ruleNotNegative,
    ruleIsNumber,
    loadContracts() {
      this.showChart = false;
      let facilityId = this.$route.params.id;

      CostAnalysisService.readFacilityAnalysis(facilityId,
        (response) => {
          this.fillAnalysis(response.data);
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:update-failure',
              {name: this.$t('book/facility:cost-analysis.actual-cost')}));
        }
      );
    },
    clearCharts() {
      this.pieSeries = [];
      this.pieManualSeries = [];
      this.pieChartOptions.labels = [];
      this.barChartOptions.xaxis.categories = [];
      this.barSeries = [];
      this.contracts = [];
      this.totalCost = 0.0;
      this.totalVat = 0.0;
    },
    getClassColor(item) {
      let costDifferenceClass = 'cost-difference '
      costDifferenceClass += item.actualCost - item.cost > 0 ? 'red' : 'green';
      return costDifferenceClass;
    },
    fillAnalysis(contracts) {
      let cost = [];
      let manual = [];
      this.clearCharts();

      for(let i = 0; i < contracts.length; i++) {
        this.pieChartOptions.labels.push(this.getContractName(contracts[i].codeName));
        this.pieSeries.push(parseFloat(contracts[i].cost));

        if (contracts[i].actualCost > 0) {
          this.pieManualSeries.push(parseFloat(contracts[i].actualCost));
        }

        cost.push(parseFloat(contracts[i].cost));
        manual.push(parseFloat(contracts[i].actualCost));
        this.barChartOptions.xaxis.categories.push(this.getContractName(contracts[i].codeName));
        this.setContract(contracts[i]);
        this.totalCost += parseFloat(contracts[i].cost);
        this.totalVat += parseFloat(contracts[i].totalVat);
      }

      this.barSeries.push({name: this.$t('common:cost'), data: cost});
      this.barSeries.push({name: this.$t('book/facility:cost-analysis.actual-cost'), data: manual});
      this.showChart = true;
    },
    getContractName(code) {
      return this.$t('enums:contract-types.' + code);
    },
    setContract(contract) {
      this.contracts.push({
        codeName: contract.codeName,
        name: this.getContractName(contract.codeName),
        cost: parseFloat(contract.cost).toFixed(2),
        actualCost: parseFloat(contract.actualCost).toFixed(2),
        contracts: contract.contracts,
        totalVat: contract.totalVat
      })
    },
    saveActualCosts() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      let costsToSave = [];
      for (let i = 0; i < this.contracts.length; i++) {
        costsToSave.push({
          id: null,
          contractType: this.contracts[i].codeName,
          actualCost: this.contracts[i].actualCost,
          facility: null
        });
      }

      FacilityService.updateActualCosts(
        this.$route.params.id,
        costsToSave,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:update-success',
              {name: this.$t('book/facility:cost-analysis.actual-cost')}));
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:update-failure',
              {name: this.$t('book/facility:cost-analysis.actual-cost')}));
        }
      )
    },
    updateCharts() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }
      this.fillAnalysis(this.contracts);
    },
    getContractUrl() {
      return this.$router.resolve({name: 'FacilityContractInfoTab', params:{id: this.$route.params.id}}).href;
    }
  },
  mounted() {
    this.loadContracts();
  }
}
</script>

<style lang="scss">
 .price-table {

   .cost-difference{
     height: 100%;
     width: 50%;
   }

   .actual-cost {
     padding: 0;

     .v-input {
       padding: 0;
       margin: 0;
     }
   }
 }
</style>